<template>
  <b-modal
    :visible="visible"
    :title="$t('shiftHandover.title')"
    size="lg"
    centered
    lazy
    :ok-title="$t('shiftHandover.confirm')"
    :cancel-title="$t('common.cancel')"
    :ok-disabled="!canSubmit || saving"
    @shown="onShown"
    @hide="onHide"
    @ok="submit"
  >
    <div class="form-group">
      <label>{{ $t('shiftHandover.destinationLabel') }}</label>
      <v-select
        v-model="toUserId"
        :options="peers"
        :reduce="p => p.id"
        label="name"
        :loading="loadingPeers"
        :placeholder="$t('shiftHandover.destinationPlaceholder')"
        class="vselect"
      />
      <small v-if="!loadingPeers && !peers.length" class="text-muted d-block mt-1">
        {{ $t('shiftHandover.noPeers') }}
      </small>
    </div>

    <div class="form-group">
      <label>{{ $t('shiftHandover.occurrencesLabel') }}</label>
      <div v-if="!occurrences.length" class="text-muted" style="font-size: 12.5px">
        {{ $t('shiftHandover.noOccurrences') }}
      </div>
      <div v-else class="shift-handover__list">
        <label
          v-for="occ in occurrences"
          :key="occ.id"
          class="shift-handover__item"
        >
          <input
            v-model="selectedOccurrenceIds"
            type="checkbox"
            :value="occ.id"
          >
          <span class="shift-handover__item-protocol">{{ occ.protocol }}</span>
          <span class="shift-handover__item-address text-muted">{{ occ.requester_name }} — {{ occ.address }}</span>
        </label>
      </div>
    </div>

    <div class="form-group">
      <label>{{ $t('shiftHandover.passwordLabel') }}</label>
      <input
        v-model="password"
        type="password"
        class="form-control"
        autocomplete="new-password"
        :placeholder="$t('shiftHandover.passwordPlaceholder')"
      >
      <small class="text-muted d-block mt-1">
        {{ $t('shiftHandover.passwordHint') }}
      </small>
    </div>

    <small v-if="error" class="text-danger d-block mt-1">{{ error }}</small>
  </b-modal>
</template>

<script>
import vSelect from 'vue-select'
import ApiErrorMixin from '@/mixins/ApiErrorMixin'

export default {
  name: 'ShiftHandoverModal',

  components: { vSelect },

  mixins: [ApiErrorMixin],

  props: {
    visible: { type: Boolean, default: false },
    occurrences: { type: Array, default: () => [] },
  },

  data() {
    return {
      peers: [],
      loadingPeers: false,
      toUserId: null,
      selectedOccurrenceIds: [],
      password: '',
      saving: false,
      error: null,
    }
  },

  computed: {
    canSubmit() {
      return !!this.toUserId && this.selectedOccurrenceIds.length > 0 && !!this.password
    },
  },

  methods: {
    async onShown() {
      this.error = null
      this.toUserId = null
      this.selectedOccurrenceIds = []
      this.password = ''
      this.loadingPeers = true
      try {
        const res = await this.$http.get(`${this.$store.state.api}shift-handover/peers`)
        this.peers = res.data.data || []
      } catch (e) {
        this.peers = []
      } finally {
        this.loadingPeers = false
      }
    },

    onHide() {
      this.$emit('update:visible', false)
    },

    async submit(bvModalEvent) {
      bvModalEvent.preventDefault()
      if (!this.canSubmit) return

      this.saving = true
      this.error = null
      try {
        const res = await this.$http.post(`${this.$store.state.api}shift-handover/transfer`, {
          to_user_id: this.toUserId,
          occurrence_ids: this.selectedOccurrenceIds,
          password: this.password,
        })
        this.$message(this.$t('regulacao.messages.successTitle'), this.$t('shiftHandover.saveSuccess'), 'success')
        this.$emit('transferred', res.data.data)
        this.$emit('update:visible', false)
      } catch (e) {
        this.error = (e.response && typeof e.response.data === 'string')
          ? e.response.data
          : this.apiErrorText(e, this.$t('shiftHandover.saveError'))
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style scoped>
.shift-handover__list {
  max-height: 260px;
  overflow-y: auto;
  border: 1px solid #ebe9f1;
  border-radius: 6px;
  padding: 4px 8px;
}

.shift-handover__item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 4px;
  margin-bottom: 0;
  font-weight: normal;
  cursor: pointer;
  border-bottom: 1px solid #f4f3f7;
}

.shift-handover__item:last-child {
  border-bottom: none;
}

.shift-handover__item-protocol {
  font-weight: 700;
  font-size: 12.5px;
  white-space: nowrap;
}

.shift-handover__item-address {
  font-size: 12px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
